import { Eye, EyeOff, ShieldCheck, UserRound, Wrench } from "lucide-react";

import { formatMinutes } from "../../shared/duration";

import type { CollectionColumn, CollectionRecord } from "../components/CollectionView";
import type { RouterOutputs } from "./trpc";

type TeamMember = RouterOutputs["team"]["list"][number];
type CatalogCategory = RouterOutputs["catalog"]["list"][number];

const roleLabels = { owner: "Titolare", manager: "Responsabile", collaborator: "Collaboratore" } as const;

export const teamColumns: CollectionColumn[] = [
  { key: "email", label: "Email" },
  { key: "ruolo", label: "Ruolo" },
  { key: "stato", label: "Stato" },
  { key: "visibilita", label: "Accesso" },
  { key: "strutture", label: "Strutture assegnate" },
];

export const catalogColumns: CollectionColumn[] = [
  { key: "categoria", label: "Categoria" },
  { key: "colore", label: "Colore" },
  { key: "stato", label: "Stato" },
  { key: "durata", label: "Durata prevista" },
  { key: "istruzioni", label: "Istruzioni" },
  { key: "fotoPrima", label: "Foto prima" },
  { key: "fotoDopo", label: "Foto dopo" },
];

export function toTeamRecord(
  member: TeamMember,
  onAccessChange: (input: { role: TeamMember["role"]; canViewAllStructures: boolean }) => void,
  index?: number,
): CollectionRecord {
  const assignedSites = member.assignedSiteNames.join(", ");
  const visibility = member.canViewAllStructures ? "Tutte le strutture" : "Solo strutture assegnate";

  return {
    id: member.userId,
    number: index === undefined ? undefined : String(index + 1).padStart(2, "0"),
    typeLabel: roleLabels[member.role],
    icon: <UserRound aria-hidden="true" />,
    title: member.name,
    cells: {
      email: { text: member.email },
      ruolo: {
        text: roleLabels[member.role],
        content: (
          <label className="select-control">
            <span>Ruolo</span>
            <select
              aria-label={`Ruolo di ${member.name}`}
              value={member.role}
              onChange={(event) => onAccessChange({ role: event.target.value as TeamMember["role"], canViewAllStructures: member.canViewAllStructures })}
            >
              {Object.entries(roleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        ),
      },
      stato: { text: member.active ? "Attivo" : "Disattivato" },
      visibilita: {
        text: visibility,
        content: (
          <label className="permission-toggle">
            <input
              type="checkbox"
              checked={member.canViewAllStructures}
              onChange={(event) => onAccessChange({ role: member.role, canViewAllStructures: event.target.checked })}
            />
            <span className="permission-toggle__switch" />
            <span>{member.canViewAllStructures ? <Eye size={18} /> : <EyeOff size={18} />}<strong>Vede tutte le strutture</strong><small>{visibility}</small></span>
          </label>
        ),
      },
      strutture: { text: assignedSites || (member.canViewAllStructures ? "Tutte le strutture" : "Nessuna struttura assegnata") },
    },
    actions: <span className={`role-chip role-chip--${member.role}`}><ShieldCheck size={15} /> {roleLabels[member.role]}</span>,
  };
}

export function toCatalogRecords(categories: CatalogCategory[]): CollectionRecord[] {
  let position = 0;
  const nextNumber = () => String((position += 1)).padStart(2, "0");

  return categories.flatMap<CollectionRecord>((category) => {
    if (category.jobTypes.length === 0) {
      return [{
        id: `${category.id}:empty`,
        number: nextNumber(),
        typeLabel: category.name,
        icon: <Wrench aria-hidden="true" />,
        title: "Nessun tipo di lavoro",
        cells: {
          categoria: { text: category.name },
          colore: { text: category.color },
          stato: null,
          durata: null,
          istruzioni: null,
          fotoPrima: null,
          fotoDopo: null,
        },
      }];
    }

    return category.jobTypes.map((jobType) => ({
      id: jobType.id,
      number: nextNumber(),
      typeLabel: category.name,
      icon: <Wrench aria-hidden="true" />,
      title: jobType.name,
      cells: {
        categoria: { text: category.name },
        colore: { text: category.color },
        stato: { text: jobType.active ? "Attivo" : "Archiviato" },
        durata: jobType.expectedMinutes ? { text: formatMinutes(jobType.expectedMinutes) } : null,
        istruzioni: jobType.instructions ? { text: jobType.instructions, expandable: true } : null,
        fotoPrima: { text: jobType.requiresBeforePhoto ? "Obbligatoria" : "Non richiesta" },
        fotoDopo: { text: jobType.requiresAfterPhoto ? "Obbligatoria" : "Non richiesta" },
      },
    }));
  });
}
